import React, { useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import { FaSave, FaArrowLeft, FaPlus } from "react-icons/fa";

const AssetAddChild = () => {
  const navigate = useNavigate();
  const [{ errorLogApi }] = useAtom(gbState);
  
  // Form state with validation
  const [formData, setFormData] = useState({
    asset_name: "",
    description: "",
    status: true,
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  
  // Loading state for submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const validateForm = () => {
    const newErrors = {};
    if (!formData.asset_name || formData.asset_name.trim() === "") {
      newErrors.asset_name = "Item/Asset name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = ({ currentTarget: input }) => {
    // Clear error when user starts typing
    if (errors[input.name]) {
      setErrors({
        ...errors,
        [input.name]: null
      });
    }
    
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  const handleAssetAddition = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      const userToken = localStorage.getItem("token");
      const response = await axios({
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/asset/add`,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        data: { ...formData },
      });
      
      toast.success("Asset created successfully!");
      navigate("/assets");
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to create asset";
      toast.error(typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage);
      errorLogApi(error, "AssetAddChild", "/asset/add");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Common styles
  const inputStyle = `w-full p-3 font-sans text-sm border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`;
  const buttonBaseStyle = `flex items-center justify-center gap-2 px-4 py-2 text-white rounded-md transition-all duration-200 font-medium`;

  return (
    <div className="flex flex-col w-full bg-gray-50 min-h-screen">
      <div className="p-6 w-full max-w-4xl mx-auto">
        {/* Header with title and actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Add New Asset</h1>
          
          <div className="flex gap-3">
            <button
              className={`${buttonBaseStyle} bg-gray-600 hover:bg-gray-700`}
              onClick={() => navigate(-1)}
              disabled={isSubmitting}
            >
              <FaArrowLeft size={16} />
              <span>Back</span>
            </button>
            
            <button
              className={`${buttonBaseStyle} ${isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              onClick={handleAssetAddition}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FaSave size={16} />
                  <span>Save Asset</span>
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            <form onSubmit={(e) => { e.preventDefault(); handleAssetAddition(); }}>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Item/Asset Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="asset_name"
                    value={formData.asset_name}
                    onChange={handleChange}
                    placeholder="Enter asset name"
                    className={`${inputStyle} ${errors.asset_name ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                  />
                  {errors.asset_name && (
                    <p className="mt-1 text-sm text-red-500">{errors.asset_name}</p>
                  )}
                </div>
                
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Enter asset description (optional)"
                    className={inputStyle}
                  />
                </div>
                
                <div className="mt-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="status"
                      checked={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Asset is active and available</span>
                  </label>
                </div>
              </div>
            </form>
          </div>
        </div>
        
        {/* Helpful tips */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Tips:</h3>
          <ul className="text-sm text-blue-700 list-disc pl-5 space-y-1">
            <li>Provide a clear, descriptive name for easy identification</li>
            <li>Include details like model number, purchase date, or location in the description</li>
            <li>Assets are active by default</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AssetAddChild;
